import React, { FC, useRef } from 'react';
import { PercentCrop } from 'react-image-crop';
import { Box, BoxProps } from '@withjoy/joykit';
import { useProgressiveImageUrl } from '../useProgressiveImageLoading';
import { RenditionSize } from '@shared/utils/photoRendition';
import { useDrawCroppedImageToCanvas } from './useDrawCroppedImageToCanvas';

interface CroppedProgressiveImageProps {
  photoUrl: string;
  lowResRendition?: RenditionSize;
  highResRendition?: RenditionSize;
  width: number;
  height: number;
  cropInfo: PercentCrop;
  containerStyles?: BoxProps['style'];
}

export const CroppedProgressiveImage: FC<CroppedProgressiveImageProps> = ({
  photoUrl,
  lowResRendition = 'medium',
  highResRendition = 'full',
  width,
  height,
  cropInfo,
  containerStyles
}) => {
  const { lowResUrl, highResUrl, isHighResLoaded } = useProgressiveImageUrl({
    photoUrl,
    lowResRendition,
    highResRendition
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { hasError } = useDrawCroppedImageToCanvas({ canvasRef, cropInfo, height, width, imageUrl: isHighResLoaded ? highResUrl : lowResUrl });

  // Fallback to background image if there's an error
  if (hasError) {
    // Use lowResUrl if available, else highResUrl, else just a color
    const fallbackBg = lowResUrl ? `url("${lowResUrl}") lightgray 50%` : highResUrl ? `url("${highResUrl}") lightgray 50%` : 'lightgray';

    return <Box width={width} height={height} overflow="hidden" background={fallbackBg} backgroundSize="cover" backgroundPosition="center" backgroundRepeat="no-repeat" />;
  }

  return (
    <Box width={width} height={height} position="relative" style={containerStyles}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
    </Box>
  );
};
